import React from 'react';
import { localize } from '@deriv-com/translations';
import Accounts from '../../_common/base/accounts';
import { useApp } from '../contexts/AppContext';

const AccountSwitcher = ({ isVisible, onClose }) => {
    const {
        allAccounts,
        accountInfo,
        switchAccount,
        getAccountTypeDisplay,
        getFormattedBalance,
        getAccountBalance,
        conversion,
    } = useApp();

    const [activeTab, setActiveTab] = React.useState(
        // Same authoritative is_virtual/account_type source the account list
        // below uses — the VR prefix mislabels v2 DOT demo accounts as real.
        Accounts.isDemoLoginId(accountInfo.loginid, allAccounts[accountInfo.loginid]) ? 'demo' : 'real'
    );
    
    // Track if mobile view
    const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
    
    React.useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    if (!isVisible) return null;

    // Close modal when clicking outside
    const handleContainerClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    // Authoritative demo/real flag is set on each account at storage time
    // (account_type === 'demo' for v2, loginid prefix for legacy). v2 demo
    // accounts use a DOT prefix that the legacy VR/VRTC check misses, so
    // prefer is_virtual and fall back to the prefix only when missing.
    const accounts = Object.values(allAccounts);
    const isDemo = acc => (
        typeof acc.is_virtual === 'boolean' || typeof acc.is_virtual === 'number'
            ? Boolean(acc.is_virtual)
            : /^VR/.test(acc.loginid || '')
    );
    const realAccounts = accounts.filter(acc => !isDemo(acc));
    const demoAccounts = accounts.filter(isDemo);

    const displayedAccounts = activeTab === 'real' ? realAccounts : demoAccounts;

    const handleSwitch = (loginid) => {
        switchAccount(loginid);
        onClose();
    };

    // Demo-to-Real Conversion Logic
    const isConversionActive = conversion.doesClientExist();
    const vrtcLoginId = conversion.getVRTCLoginId();
    const realLoginId = conversion.getRealLoginId();
    const balanceOffset = conversion.getBalanceOffset();

    const getDisplayBalance = (acc) => {
        // Get balance from the all_accounts_balance response (like multi_platform_sites)
        const accountBalance = getAccountBalance(acc.loginid);
        let balance = accountBalance.balance || 0;
        const currency = accountBalance.currency || acc.currency || 'USD';
        
        if (isConversionActive && acc.loginid === realLoginId && vrtcLoginId) {
            // If on conversion real account, show demo balance minus offset
            const vrtcBalance = getAccountBalance(vrtcLoginId);
            balance = (vrtcBalance.balance || 0) - balanceOffset;
        }
        
        return getFormattedBalance(balance, currency);
    };

    // Mobile dialog structure
    if (isMobile) {
        return (
            <div className='account-switcher-mobile-dialog' onClick={handleContainerClick}>
                <div className='account-switcher-mobile-content' onClick={e => e.stopPropagation()}>
                    <div className='account-switcher-mobile-header'>
                        <div className='account-switcher-mobile-title'>{localize('Select account')}</div>
                        <button className='account-switcher-mobile-close' onClick={onClose}>×</button>
                    </div>
                    <div className='account-switcher-tabs'>
                        <div
                            className={`account-switcher-tab ${activeTab === 'real' ? 'active' : ''}`}
                            onClick={() => setActiveTab('real')}
                        >
                            {localize('Real')}
                        </div>
                        <div
                            className={`account-switcher-tab ${activeTab === 'demo' ? 'active' : ''}`}
                            onClick={() => setActiveTab('demo')}
                        >
                            {localize('Demo')}
                        </div>
                    </div>
                    <div className='account-switcher-list'>
                        <div className='account-switcher-list-header'>
                            {activeTab === 'real' ? localize('Deriv Accounts') : localize('Demo Accounts')}
                        </div>
                        {displayedAccounts.length === 0 ? (
                            <div className='account-switcher-empty'>
                                {localize('No accounts found')}
                            </div>
                        ) : (
                            displayedAccounts.map(acc => (
                                <div
                                    key={acc.loginid}
                                    className={`account-switcher-item ${acc.loginid === accountInfo.loginid ? 'active' : ''}`}
                                    onClick={() => handleSwitch(acc.loginid)}
                                >
                                    <div className='account-switcher-item-left'>
                                        <div className='account-switcher-item-currency'>
                                            {acc.currency || (acc.is_virtual ? 'Demo' : 'USD')}
                                        </div>
                                        <div className='account-switcher-item-id'>
                                            {acc.loginid}
                                        </div>
                                    </div>
                                    <div className='account-switcher-item-right'>
                                        <div className='account-switcher-item-balance'>
                                            {getDisplayBalance(acc)}
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // Desktop dropdown structure
    return (
        <div className='account-switcher-container' onClick={handleContainerClick}>
            <div className='account-switcher-body' onClick={e => e.stopPropagation()}>
                <div className='account-switcher-tabs'>
                    <div
                        className={`account-switcher-tab ${activeTab === 'real' ? 'active' : ''}`}
                        onClick={() => setActiveTab('real')}
                    >
                        {localize('Real')}
                    </div>
                    <div
                        className={`account-switcher-tab ${activeTab === 'demo' ? 'active' : ''}`}
                        onClick={() => setActiveTab('demo')}
                    >
                        {localize('Demo')}
                    </div>
                </div>
                
                <div className='account-switcher-list'>
                    <div className='account-switcher-list-header'>
                        {activeTab === 'real' ? localize('Deriv Accounts') : localize('Demo Accounts')}
                    </div>
                    
                    {displayedAccounts.length === 0 ? (
                        <div className='account-switcher-empty'>
                            {localize('No accounts found')}
                        </div>
                    ) : (
                        displayedAccounts.map(acc => (
                            <div
                                key={acc.loginid}
                                className={`account-switcher-item ${acc.loginid === accountInfo.loginid ? 'active' : ''}`}
                                onClick={() => handleSwitch(acc.loginid)}
                            >
                                <div className='account-switcher-item-left'>
                                    <div className='account-switcher-item-currency'>
                                        {acc.currency || (acc.is_virtual ? 'Demo' : 'USD')}
                                    </div>
                                    <div className='account-switcher-item-id'>
                                        {acc.loginid}
                                    </div>
                                </div>
                                <div className='account-switcher-item-right'>
                                    <div className='account-switcher-item-balance'>
                                        {getDisplayBalance(acc)}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

export default AccountSwitcher;
